/** Codes ban renvoyés par l’API auth / middleware. */
export const BAN_ERROR_CODES = new Set([
  'ACCOUNT_BANNED',
  'EMAIL_BANNED',
  'IP_BANNED',
])

export type BanPublicPayload = {
  code?: string
  message?: string
  reason?: string | null
  expiresAt?: string | null
}

const STORAGE_KEY = 'ban_status'

export function isBanErrorCode(code: unknown): boolean {
  return typeof code === 'string' && BAN_ERROR_CODES.has(code)
}

export function extractBanPayload(err: {
  code?: unknown
  message?: unknown
  reason?: unknown
  expiresAt?: unknown
  data?: unknown
}): BanPublicPayload | null {
  const data =
    err?.data && typeof err.data === 'object' ? (err.data as Record<string, unknown>) : null
  const code = (data?.code ?? err?.code) as unknown
  if (!isBanErrorCode(code)) return null
  return {
    code: String(code),
    message: String(data?.message ?? err?.message ?? '') || undefined,
    reason: (data?.reason ?? err?.reason) != null ? String(data?.reason ?? err?.reason) : null,
    expiresAt:
      (data?.expiresAt ?? err?.expiresAt) != null
        ? String(data?.expiresAt ?? err?.expiresAt)
        : null,
  }
}

export function storeBanPayload(payload: BanPublicPayload): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* ignore quota */
  }
}

export function readStoredBanPayload(): BanPublicPayload | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as BanPublicPayload
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

export function clearStoredBanPayload(): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

/** Redirection dure vers /banned (comme maintenance / cooldown). */
export function redirectToBannedPage(payload: BanPublicPayload): void {
  storeBanPayload(payload)
  if (typeof window === 'undefined') return
  if (window.location.pathname === '/banned') return
  window.location.href = '/banned'
}

export function banPayloadFromQuery(query: Record<string, unknown>): BanPublicPayload {
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
  return {
    code: str(query.code),
    message: str(query.message),
    reason: str(query.reason) ?? null,
    expiresAt: str(query.expiresAt) ?? null,
  }
}

export function formatBanRemaining(
  expiresAtIso: string | null | undefined,
  nowMs = Date.now(),
): { permanent: boolean; expired: boolean; totalSeconds: number; labelFr: string; labelEn: string } {
  if (!expiresAtIso) {
    return {
      permanent: true,
      expired: false,
      totalSeconds: 0,
      labelFr: 'Permanent',
      labelEn: 'Permanent',
    }
  }
  const end = new Date(expiresAtIso).getTime()
  if (!Number.isFinite(end)) {
    return {
      permanent: true,
      expired: false,
      totalSeconds: 0,
      labelFr: 'Permanent',
      labelEn: 'Permanent',
    }
  }
  const totalSeconds = Math.max(0, Math.floor((end - nowMs) / 1000))
  if (totalSeconds <= 0) {
    return {
      permanent: false,
      expired: true,
      totalSeconds: 0,
      labelFr: 'Terminé',
      labelEn: 'Ended',
    }
  }
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  let labelFr: string
  let labelEn: string
  if (days > 0) {
    labelFr = `${days} j ${hours} h`
    labelEn = `${days}d ${hours}h`
  } else if (hours > 0) {
    labelFr = `${hours} h ${minutes} min`
    labelEn = `${hours}h ${minutes}m`
  } else if (minutes > 0) {
    labelFr = `${minutes} min ${seconds} s`
    labelEn = `${minutes}m ${seconds}s`
  } else {
    labelFr = `${seconds} s`
    labelEn = `${seconds}s`
  }
  return { permanent: false, expired: false, totalSeconds, labelFr, labelEn }
}

export function buildBannedRedirectUrl(
  frontendUrl: string,
  payload: BanPublicPayload,
): string {
  const params = new URLSearchParams()
  if (payload.code) params.set('code', payload.code)
  if (payload.expiresAt) params.set('expiresAt', payload.expiresAt)
  if (payload.message) params.set('message', payload.message.slice(0, 160))
  if (payload.reason) params.set('reason', payload.reason.slice(0, 240))
  const q = params.toString()
  return q ? `${frontendUrl}/banned?${q}` : `${frontendUrl}/banned`
}
